/**
 * argile-hubspot.js — pont entre les formulaires du thème et HubSpot.
 *
 * POURQUOI UN PONT ET PAS UN FORMULAIRE HUBSPOT EMBARQUÉ
 *
 * Les deux formulaires du site (contact, réservation de conseil) utilisent
 * `{% form 'contact' %}`, le formulaire natif Shopify. On ne peut pas les
 * remplacer par un formulaire HubSpot embarqué :
 *   — la réservation est un parcours en 4 étapes avec champs conditionnels,
 *     non reproductible dans un formulaire HubSpot ;
 *   — l'e-mail que reçoit aujourd'hui la boutique doit continuer d'arriver ;
 *   — le captcha Shopify est actif sur les formulaires de contact, ce qui
 *     interdit toute soumission AJAX : le POST natif DOIT rester le chemin
 *     principal.
 *
 * D'où ce montage : au moment de la soumission, on envoie une COPIE à HubSpot
 * en parallèle, puis on laisse le POST natif partir. Si HubSpot est injoignable,
 * lent, ou mal configuré, la soumission Shopify aboutit quand même. Aucune
 * régression possible sur l'existant : c'est la règle qui a guidé tout ce code.
 *
 * `keepalive: true` demande au navigateur de terminer la requête même si la page
 * navigue immédiatement après — ce qui est précisément ce qui se passe ici.
 *
 * CONTRAINTE DE L'API DE SOUMISSION HUBSPOT
 *
 * Tout champ envoyé doit correspondre à une propriété HubSpot EXISTANTE : les
 * champs inconnus font échouer la soumission entière. On ne peut donc pas
 * « envoyer l'info et la laisser dormir dans la soumission ». D'où le parti pris
 * de MAPPING ci-dessous : peu de propriétés structurées (ce qui sert à filtrer),
 * et tout le reste concaténé dans le message (ce qui sert au rappel).
 *
 * Voir docs/HANDOFF-hubspot.md §5 pour le mapping complet et sa justification.
 */

(function () {
  'use strict';

  // ── Configuration, émise par snippets/argile-hubspot.liquid ────────────────
  var configNode = document.getElementById('argile-hubspot-config');
  if (!configNode) return;

  var config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (error) {
    return;
  }

  if (!config || !config.portalId) return;

  /**
   * Correspondance champ Shopify → propriété HubSpot.
   *
   * Les clés sont les attributs `name` EXACTS des formulaires (accents compris).
   * Les valeurs sont les noms internes des propriétés HubSpot.
   *
   * ⚠️ Les propriétés `argile_*` n'existent pas encore dans le portail : elles
   * sont à créer avant la recette (voir docs/HANDOFF-hubspot.md §5). Tant
   * qu'elles n'existent pas, HubSpot rejettera les soumissions — c'est sans
   * conséquence pour l'utilisateur, qui verra son formulaire partir normalement.
   */
  /** Propriétés standard HubSpot — existent toujours, aucune création nécessaire. */
  var MAPPING_STANDARD = {
    'contact[Prénom]': 'firstname',
    'contact[Nom]': 'lastname',
    'contact[email]': 'email',
    'contact[Numéro de téléphone]': 'phone'
  };

  /**
   * Propriétés custom — envoyées SEULEMENT quand `customPropertiesReady` est vrai
   * dans la configuration.
   *
   * Pourquoi ce garde-fou : HubSpot rejette la soumission ENTIÈRE si un seul champ
   * lui est inconnu. Envoyer ces onze propriétés avant qu'elles n'existent (comme
   * propriétés ET comme champs du formulaire) ferait échouer toutes les
   * remontées, y compris les champs standard qui, eux, marcheraient. On étage donc
   * la mise en service : d'abord le tuyau avec le minimum, ensuite le reste.
   */
  var MAPPING_CUSTOM = {
    // Formulaire de réservation.
    'contact[Profil]': 'argile_profil_client',
    'contact[Type de conseil]': 'argile_type_conseil',
    'contact[Accompagné par un professionnel]': 'argile_accompagne_pro',
    'contact[Type de professionnel]': 'argile_type_professionnel',
    'contact[Localisation du projet]': 'argile_localisation_projet',
    'contact[Type de projet]': 'argile_type_projet',
    'contact[Superficie]': 'argile_superficie',
    'contact[Lieu du rendez-vous]': 'argile_lieu_rdv',

    // Formulaire de contact.
    'contact[Sujet]': 'argile_sujet_demande',
    'contact[Qui êtes-vous]': 'argile_qui_etes_vous',

    // Acceptation des CGV (vaut « Oui »). La case est `required` côté thème,
    // donc aucune soumission ne peut exister sans elle : la preuve est déjà
    // structurelle, et HubSpot conserve chaque soumission horodatée. On la
    // stocke quand même pour la rendre visible et filtrable sur la fiche.
    //
    // Ce n'est PAS un registre de consentement opposable — celui-ci passerait
    // par le bloc `legalConsentOptions` de l'API et suppose d'activer la
    // confidentialité des données au niveau du portail, réglage global partagé
    // avec les équipes FR et UK. À rouvrir le jour où le besoin est exprimé.
    'contact[Conditions acceptées]': 'argile_cgv_acceptees'
  };

  /** Mapping effectif, selon l'étage de mise en service. */
  var MAPPING = Object.assign({}, MAPPING_STANDARD, config.customPropertiesReady ? MAPPING_CUSTOM : {});

  /**
   * Champs regroupés dans le message plutôt que transformés en propriétés.
   *
   * Ils servent au rappel téléphonique, pas au filtrage : en faire huit
   * propriétés supplémentaires alourdirait un portail qui en compte déjà 282
   * sans que personne ne s'en serve pour segmenter.
   *
   * L'ordre ci-dessous est celui du récapitulatif envoyé — il est pensé pour
   * être lu par un commercial qui rappelle, pas par une machine.
   */
  var DANS_LE_MESSAGE = [
    'contact[Nom du projet]',
    'contact[Pièces à peindre]',
    'contact[Autre pièce]',
    'contact[Moment de la journée]',
    'contact[Jours souhaités]',
    'contact[Contrainte de date]'
  ];

  /** Libellés lisibles pour le récapitulatif, sans le préfixe `contact[…]`. */
  function libelle(name) {
    return name.replace(/^contact\[/, '').replace(/\]$/, '');
  }

  /** Lit le cookie d'attribution HubSpot, s'il existe. */
  function jetonAttribution() {
    var match = document.cookie.match(/(^|;)\s*hubspotutk=([^;]+)/);
    return match ? match[2] : null;
  }

  /**
   * Construit la charge utile attendue par l'API de soumission HubSpot.
   * Renvoie null si le formulaire n'a pas d'e-mail : sans e-mail, HubSpot ne
   * peut pas créer de contact, la requête serait rejetée pour rien.
   */
  function construirePayload(form) {
    var data = new FormData(form);
    var fields = [];
    var extras = [];
    var email = '';

    Object.keys(MAPPING).forEach(function (name) {
      var value = data.get(name);
      if (value === null || value === '') return;

      if (MAPPING[name] === 'email') email = value;

      fields.push({ objectTypeId: '0-1', name: MAPPING[name], value: String(value) });
    });

    if (!email) return null;

    // Le corps du message, augmenté des champs de rappel.
    var message = data.get('contact[body]') || '';

    DANS_LE_MESSAGE.forEach(function (name) {
      var value = data.get(name);
      if (value === null || value === '') return;
      extras.push(libelle(name) + ' : ' + value);
    });

    if (extras.length) {
      message = (message ? message + '\n\n' : '') + extras.join('\n');
    }

    if (message) {
      fields.push({ objectTypeId: '0-1', name: 'message', value: String(message) });
    }

    var context = {
      pageUri: window.location.href,
      pageName: document.title
    };

    var hutk = jetonAttribution();
    if (hutk) context.hutk = hutk;

    return { fields: fields, context: context };
  }

  /**
   * Envoie la copie à HubSpot sans jamais retarder la soumission Shopify.
   *
   * Aucune gestion d'erreur volontairement : on ne peut rien faire d'utile d'un
   * échec à cet instant (la page est déjà en train de naviguer), et surtout on
   * ne veut pas qu'une exception ici empêche le POST natif de partir.
   */
  function envoyer(guid, payload) {
    var url =
      'https://api.hsforms.com/submissions/v3/integration/submit/' +
      encodeURIComponent(config.portalId) +
      '/' +
      encodeURIComponent(guid);

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function () {});
    } catch (error) {
      /* silencieux — voir commentaire ci-dessus */
    }
  }

  // ── Branchement ────────────────────────────────────────────────────────────
  // On écoute `submit`, qui ne se déclenche qu'APRÈS la validation native du
  // navigateur : pas de copie envoyée pour un formulaire incomplet.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-hubspot-form]')) return;

      var guid = config.forms && config.forms[form.getAttribute('data-hubspot-form')];
      if (!guid) return; // formulaire non encore créé côté HubSpot : on ne fait rien

      var payload = construirePayload(form);
      if (!payload) return;

      envoyer(guid, payload);
      // Pas de preventDefault : le POST natif Shopify part normalement.
    },
    false
  );
})();
